// Deal value, utilisation and cost — the typed doors to
// 20261104100000_money_utilisation.sql. The server decides everything; this file
// names the shapes and calls it.
//
//   · An hour costs the person's salary ÷ their contracted hours in the calendar
//     year (HR's 'Cost / hour'). Freelancers are left out of every cost, and
//     `freelancer_hours` says how many hours that leaves out ("excludes
//     freelancers"); `uncosted_hours` are staff with no INR salary on file.
//   · Money is whole rupees (INR). A non-INR deal or salary is never converted:
//     the figure comes back null and `currency` says why — show a dash and a note.
//   · Null anywhere means "no real figure": show "—", never 0.
//   · Project money answers only people:full AND (pipeline:full OR the project's
//     manager): owner and admin in the seeded matrix. `data: null` = not yours.
//   · utilisation() is hours only, with Capacity's who-sees-whom rule; a
//     person's logged hours come back null unless they are the caller's to read
//     (themselves, timesheets:full, or below them in the line).
//   · Month to date runs to yesterday: today's hours land at check-out.
// Format money with lib/money.ts (fmtINR headlines, fmtINRFull tables).
import { supabase } from './supabase';
import type { Fetched } from './issueReports';

export type { Fetched };

/** policy.finance.targetUtilisation when unset. */
export const DEFAULT_TARGET_UTILISATION = 80;

/** One person in one org month (utilisation()). Hours only. */
export type UtilisationRow = {
  profile_id: string;
  /** The org month's first day, YYYY-MM-01. */
  month: string;
  /** Contracted day × working days − approved leave, clamped to joining and exit, cut to the range and to yesterday. */
  available_hours: number;
  /** Every project-timer hour (billable or not); null: not the caller's to read. */
  project_hours: number | null;
  /** Null exactly when project_hours is. */
  billable_hours: number | null;
};

/** project_money(): one project over its whole life. */
export type ProjectMoney = {
  project_id: string;
  /** The linked deal's amount in INR; null: no deal, or the deal isn't in INR. */
  deal_value: number | null;
  /** The deal's currency ('INR' when Zoho sent none); null: no deal. */
  currency: string | null;
  stage: string | null;
  is_billable: boolean;
  hours: number;
  billable_hours: number;
  /** Hours freelancers logged: in `hours`, left out of every cost. */
  freelancer_hours: number;
  /** Staff hours with no INR salary on file: in `hours`, left out of cost. */
  uncosted_hours: number;
  cost: number;
  cost_billable: number;
  /** deal − cost; null when there is no INR deal or the project doesn't bill. */
  margin: number | null;
  margin_pct: number | null;
  /** cost ÷ deal × 100. */
  burn_pct: number | null;
  /** deal ÷ all project hours. */
  earned_per_hour: number | null;
  /** Σ estimates of leaf work items (no epic double count); null: nothing estimated. */
  hours_estimate: number | null;
  /** Most hours first. rate × hours = cost; both null for freelancers and uncosted staff. */
  by_person: {
    profile_id: string;
    name: string | null;
    hours: number;
    rate: number | null;
    cost: number | null;
    freelancer: boolean;
  }[];
  /** Oldest first; cost excludes freelancers and uncosted staff. */
  by_month: { month: string; hours: number; cost: number }[];
};

/** money_overview() projects[]: life-to-date money plus the range's hours and cost. */
export type MoneyProject = Omit<ProjectMoney, 'hours_estimate' | 'by_person' | 'by_month'> & {
  name: string;
  code: string | null;
  client: string | null;
  status: string;
  /** billable ÷ all project hours × 100. */
  billable_share: number | null;
  range_hours: number;
  range_cost: number;
};

/** money_overview() people[]: one person over the range. */
export type MoneyPerson = {
  profile_id: string;
  name: string | null;
  freelancer: boolean;
  /** ₹/h in the range's last month; null: freelancer, no CTC, or not INR. */
  rate: number | null;
  available_h: number;
  project_h: number;
  billable_h: number;
  worked_h: number;
  /** project_h ÷ available_h × 100; may pass 100. */
  utilisation: number | null;
  billable_utilisation: number | null;
  cost_on_projects: number | null;
  /** Salary for the working days in range (paid leave included, unpaid out). */
  month_cost: number | null;
  /** month_cost − cost_on_projects: idle time, leave and unallocated hours. Negative with overtime. */
  unabsorbed: number | null;
};

/** money_overview() months[] and totals: cost and utilisation, never revenue. */
export type MoneyPeriod = {
  available_h: number;
  project_h: number;
  billable_h: number;
  freelancer_hours: number;
  uncosted_hours: number;
  salary_cost: number | null;
  cost_on_projects: number | null;
  unabsorbed: number | null;
  /** Pooled: Σ project_h ÷ Σ available_h × 100. */
  utilisation: number | null;
  billable_utilisation: number | null;
};
export type MoneyMonth = MoneyPeriod & { month: string };

export type MoneyOverview = {
  from: string;
  to: string;
  target_utilisation: number;
  projects: MoneyProject[];
  people: MoneyPerson[];
  months: MoneyMonth[];
  totals: MoneyPeriod;
};

/** dashboard_widget('utilisation'): this org month to yesterday, the scope's people. */
export type UtilisationWidget = {
  month: string;
  from: string;
  /** Yesterday: before `from` on the 1st, when nothing is counted yet. */
  to: string;
  target_utilisation: number;
  /** Pooled over the scope, as week_time is. */
  available_hours: number;
  project_hours: number;
  billable_hours: number;
  utilisation: number | null;
  billable_utilisation: number | null;
  /** Hours and utilisation null where the person's time isn't the caller's to read. */
  people: {
    profile_id: string;
    name: string | null;
    available_hours: number;
    project_hours: number | null;
    billable_hours: number | null;
    utilisation: number | null;
    billable_utilisation: number | null;
  }[];
};

/**
 * dashboard_widget('money'): this month's MoneyPeriod, plus the whole-life deal,
 * cost and margin of active projects that bill against an INR deal.
 */
export type MoneyWidget = MoneyPeriod & {
  month: string;
  from: string;
  to: string;
  target_utilisation: number;
  projects: number;
  deal_value: number | null;
  project_cost: number | null;
  margin: number | null;
  margin_pct: number | null;
};

// PGRST202: the function isn't deployed yet (this bundle ahead of the migration).
const message = (e: { code?: string; message: string }) =>
  e.code === 'PGRST202' ? 'Cost and utilisation aren’t switched on yet.' : e.message;

/** Null data: not this caller's to see. */
export async function loadProjectMoney(projectId: string): Promise<Fetched<ProjectMoney>> {
  if (!supabase) return { ok: true, data: null };
  const { data, error } = await supabase.rpc('project_money', { p_project: projectId });
  return error
    ? { ok: false, error: message(error) }
    : { ok: true, data: data as ProjectMoney | null };
}

/** Dates are YYYY-MM-DD, at most two years apart. Null data: owner and admin only. */
export async function loadMoneyOverview(from: string, to: string): Promise<Fetched<MoneyOverview>> {
  if (!supabase) return { ok: true, data: null };
  const { data, error } = await supabase.rpc('money_overview', { p_from: from, p_to: to });
  return error
    ? { ok: false, error: message(error) }
    : { ok: true, data: data as MoneyOverview | null };
}

const num = (v: number | string | null) => (v == null ? null : Number(v));

/** Rows for everyone the caller may see on Capacity; empty without capacity:view. */
export async function loadUtilisation(
  from: string,
  to: string,
): Promise<Fetched<UtilisationRow[]>> {
  if (!supabase) return { ok: true, data: null };
  const { data, error } = await supabase.rpc('utilisation', { p_from: from, p_to: to });
  if (error) return { ok: false, error: message(error) };
  return {
    ok: true,
    data: ((data as UtilisationRow[] | null) ?? []).map((r) => ({
      profile_id: r.profile_id,
      month: r.month,
      available_hours: Number(r.available_hours),
      project_hours: num(r.project_hours),
      billable_hours: num(r.billable_hours),
    })),
  };
}

/** policy.finance.targetUtilisation (%), 80 when unset. Written at org:full through Org settings. */
export async function loadTargetUtilisation(): Promise<Fetched<number>> {
  if (!supabase) return { ok: true, data: null };
  const { data, error } = await supabase
    .from('org_settings')
    .select('policy')
    .eq('id', 1)
    .maybeSingle();
  if (error) return { ok: false, error: error.message };
  const t = (data as { policy?: { finance?: { targetUtilisation?: unknown } } } | null)?.policy
    ?.finance?.targetUtilisation;
  return { ok: true, data: typeof t === 'number' ? t : DEFAULT_TARGET_UTILISATION };
}
